import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, raidWsUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TournamentBracketPanel } from "../components/game/TournamentBracketPanel";
import { BbebbegPanel } from "../components/game/BbebbegPanel";
import { DungeonCrawlPanel } from "../components/game/DungeonCrawlPanel";
import { CompleteRaidPanel } from "../components/game/CompleteRaidPanel";
import { InfoButton } from "../components/game/InfoButton";
import { toast } from "sonner";
import { ArrowLeft, Copy, Send, Shield, Swords, CheckSquare, Plus, X, Circle, MapPinned, FileText, Target, ChevronDown } from "lucide-react";

const RAID_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/n4wydar9_Raid%20lobby.png";

export default function RaidDashboardPage() {
  const { raidId } = useParams();
  const { user, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [raid, setRaid] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [goalDraft, setGoalDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [branchName, setBranchName] = useState("");
  const [makingBranch, setMakingBranch] = useState(false);
  const [objectiveText, setObjectiveText] = useState("");
  const [objectiveAssignee, setObjectiveAssignee] = useState("");
  const [activeOptionPanel, setActiveOptionPanel] = useState(null); // null | "notes" | "objectives" | "goal"
  const socketRef = useRef(null);
  const chatScrollRef = useRef(null);

  const isMasterGm = raid?.my_role === "master_gm";
  const myMember = (raid?.members || []).find((m) => m.user_id === user?.id);

  const loadRaid = async () => {
    try {
      const { data } = await api.get(`/raids/${raidId}`);
      setRaid(data);
      setNotesDraft(data.story_notes || "");
      setGoalDraft(data.party_goal_text || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/dashboard");
    }
  };

  const loadMessages = async () => {
    const { data } = await api.get(`/raids/${raidId}/messages`);
    setMessages(data);
  };

  useEffect(() => {
    loadRaid();
    loadMessages();
  }, [raidId]);

  useEffect(() => {
    let socket;
    (async () => {
      const { data } = await api.get("/auth/ws-token");
      socket = new WebSocket(raidWsUrl(raidId, data.token));
      socketRef.current = socket;
      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "raid_update") setRaid((r) => (r ? { ...r, ...msg.data } : r));
          else if (msg.type === "raid_member_joined") setRaid((r) => (r ? { ...r, members: [...(r.members || []), msg.data] } : r));
          else if (msg.type === "raid_objective_upsert") setRaid((r) => {
            if (!r) return r;
            const exists = (r.objectives || []).some((o) => o.id === msg.data.id);
            const objectives = exists ? r.objectives.map((o) => (o.id === msg.data.id ? msg.data : o)) : [...(r.objectives || []), msg.data];
            return { ...r, objectives };
          });
          else if (msg.type === "raid_objective_delete") setRaid((r) => (r ? { ...r, objectives: (r.objectives || []).filter((o) => o.id !== msg.data.id) } : r));
          else if (msg.type === "raid_chat_message") setMessages((m) => [...m, msg.data]);
          else if (msg.type === "raid_branch_created") setRaid((r) => (r ? { ...r, branches: [...(r.branches || []), msg.data] } : r));
          else if (msg.type === "raid_branch_update") setRaid((r) => (r ? { ...r, branches: (r.branches || []).map((b) => (b.id === msg.data.id ? { ...b, ...msg.data } : b)) } : r));
          else if (msg.type === "tournament_score_update") setRaid((r) => {
            if (!r?.tournament) return r;
            const matches = r.tournament.matches.map((m) => (
              m.id === msg.data.match_id ? { ...m, scores: { ...m.scores, [msg.data.branch_id]: msg.data.new_scores } } : m
            ));
            return { ...r, tournament: { ...r.tournament, matches } };
          });
        } catch {}
      };
    })();
    return () => socket?.close();
  }, [raidId]);

  useEffect(() => {
    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const copyInvite = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${raid.invite_code}`);
    toast.success("Raid invite link copied");
  };

  const copyBranchInvite = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${code}`);
    toast.success("Table invite link copied");
  };

  const saveNotes = async () => {
    await api.patch(`/raids/${raidId}`, { story_notes: notesDraft });
    toast.success("Raid notes saved");
  };

  const saveGoal = async () => {
    await api.patch(`/raids/${raidId}`, { party_goal_text: goalDraft });
    toast.success("Shared End Goal saved");
  };

  const sendChat = async () => {
    if (!chatText.trim()) return;
    await api.post(`/raids/${raidId}/messages`, { content: chatText });
    setChatText("");
  };

  const makeBranch = async () => {
    if (!branchName.trim()) return toast.error("Give your Table a name");
    setMakingBranch(true);
    try {
      const { data } = await api.post(`/raids/${raidId}/branches`, { name: branchName.trim() });
      setRaid((r) => (r ? { ...r, branches: [...(r.branches || []).filter((b) => b.id !== data.branch.id), data.branch], my_branch_id: data.branch.id } : r));
      setBranchName("");
      toast.success("Table created — you can now enter the Raid Table");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setMakingBranch(false);
    }
  };

  const addObjective = async () => {
    if (!objectiveText.trim()) return toast.error("Give this objective some text");
    await api.post(`/raids/${raidId}/objectives`, { text: objectiveText, assigned_to_member_id: objectiveAssignee || null });
    setObjectiveText("");
    setObjectiveAssignee("");
  };

  const toggleObjective = async (obj) => {
    await api.patch(`/raids/${raidId}/objectives/${obj.id}`, { done: !obj.done });
  };

  const removeObjective = async (obj) => {
    await api.delete(`/raids/${raidId}/objectives/${obj.id}`);
  };

  const toggleSharedWaitingRoom = async (v) => {
    await api.patch(`/raids/${raidId}`, { shared_waiting_room: v });
    setRaid((r) => (r ? { ...r, shared_waiting_room: v } : r));
  };

  const toggleMasterHelperAccess = async (v) => {
    await api.patch(`/raids/${raidId}`, { master_helper_access_enabled: v });
    setRaid((r) => (r ? { ...r, master_helper_access_enabled: v } : r));
  };

  const toggleBranchHelperAccess = async (branchId, v) => {
    try {
      await api.patch(`/raids/${raidId}/branches/${branchId}/helper-access`, { enabled: v });
      setRaid((r) => (r ? { ...r, branches: (r.branches || []).map((b) => (b.id === branchId ? { ...b, helper_access_enabled: v } : b)) } : r));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const approveDungeon = async (branchId) => {
    try {
      await api.post(`/raids/${raidId}/branches/${branchId}/approve`);
      setRaid((r) => (r ? { ...r, branches: (r.branches || []).map((b) => (b.id === branchId ? { ...b, dungeon_approved: true } : b)) } : r));
      toast.success("Dungeon approved — players can now join");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const generals = (raid?.members || []).filter((m) => m.role === "general");
  const openObjectivesCount = (raid?.objectives || []).filter((o) => !o.done).length;
  const showGoalButton = raid?.raid_type === "alliance" || raid?.raid_type === "party";
  const toggleOptionPanel = (key) => setActiveOptionPanel((p) => (p === key ? null : key));
  const optionBtnClass = (active) => `flex items-center gap-1.5 text-xs uppercase tracking-wider px-3.5 py-2 rounded-full border transition-colors duration-200 ${active ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]" : "bg-black/20 border-white/10 text-[#f1e0c5]/60 hover:border-white/20"}`;

  if (!raid) return <div className="min-h-screen bg-[#151613] flex items-center justify-center text-[#f1e0c5]/50 text-sm uppercase tracking-widest">Loading Raid…</div>;

  return (
    <div className="relative min-h-screen text-[#f1e0c5] bg-[#151613] bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${RAID_BG_URL}')` }} data-testid="raid-dashboard-page">
      <div className="fixed inset-0 bg-black/70 pointer-events-none" data-testid="raid-bg-overlay" />
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <Link to="/dashboard" data-testid="raid-back-to-dashboard" className="flex items-center gap-2 text-sm text-[#f1e0c5]/60 hover:text-[#3B82F6]">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#1E3A8A]/30 text-[#3B82F6]">{isMasterGm ? "Master GM" : "General"}</span>
          {isMasterGm && (
            <button data-testid="raid-copy-invite" onClick={copyInvite} className="flex items-center gap-1.5 text-xs text-[#f1e0c5]/50 hover:text-[#3B82F6]">
              <Copy className="w-3.5 h-3.5" /> {raid.invite_code}
            </button>
          )}
        </div>
      </nav>

      <div className="relative z-10 px-6 md:px-12 py-10 max-w-6xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl flex items-center gap-3" data-testid="raid-name-heading"><Shield className="w-8 h-8 text-[#3B82F6]" /> {raid.name} <InfoButton topic={raid.raid_type || "alliance"} /></h1>
          <Button data-testid="raid-enter-table-button" onClick={() => navigate(`/game/${raid.master_game_id}`)} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm gap-2">
            <MapPinned className="w-4 h-4" /> Enter the Raid Table
          </Button>
        </div>

        <div className="space-y-2" data-testid="raid-options-bar">
          <div className="flex flex-wrap gap-2">
            <button data-testid="raid-options-notes-button" onClick={() => toggleOptionPanel("notes")} className={optionBtnClass(activeOptionPanel === "notes")}>
              <FileText className="w-3.5 h-3.5" /> Raid Notes <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${activeOptionPanel === "notes" ? "rotate-180" : ""}`} />
            </button>
            <button data-testid="raid-options-objectives-button" onClick={() => toggleOptionPanel("objectives")} className={optionBtnClass(activeOptionPanel === "objectives")}>
              <CheckSquare className="w-3.5 h-3.5" /> Objectives {openObjectivesCount > 0 && <span className="text-[9px] px-1.5 rounded-full bg-[#1E3A8A]/40 text-[#3B82F6]">{openObjectivesCount}</span>} <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${activeOptionPanel === "objectives" ? "rotate-180" : ""}`} />
            </button>
            {showGoalButton && (
              <button data-testid="raid-options-goal-button" onClick={() => toggleOptionPanel("goal")} className={optionBtnClass(activeOptionPanel === "goal")}>
                <Target className="w-3.5 h-3.5" /> Shared End Goal <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${activeOptionPanel === "goal" ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>

          {activeOptionPanel === "notes" && (
            <div className="panel-wood rounded-2xl p-5 space-y-2" data-testid="raid-options-dropdown-notes">
              <p className="text-[10px] text-[#f1e0c5]/40">The shared story so far — every General reads this to stay in sync on the big picture.</p>
              <Textarea
                data-testid="raid-notes-textarea"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                onBlur={isMasterGm ? saveNotes : undefined}
                readOnly={!isMasterGm}
                placeholder="The shared story so far…"
                className="bg-black/30 border-white/10 text-sm min-h-32"
              />
            </div>
          )}

          {activeOptionPanel === "objectives" && (
            <div className="panel-wood rounded-2xl p-5 space-y-3" data-testid="raid-options-dropdown-objectives">
              <div className="space-y-1.5">
                {(raid.objectives || []).map((o) => {
                  const canToggle = isMasterGm || o.assigned_to_member_id === myMember?.id;
                  const assignee = (raid.members || []).find((m) => m.id === o.assigned_to_member_id);
                  return (
                    <div key={o.id} data-testid={`raid-objective-${o.id}`} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 ${o.done ? "bg-black/10 opacity-50" : "bg-black/20"}`}>
                      <Checkbox data-testid={`raid-objective-checkbox-${o.id}`} checked={o.done} disabled={!canToggle} onCheckedChange={() => toggleObjective(o)} />
                      <span className={`flex-1 text-sm ${o.done ? "line-through" : ""}`}>{o.text}</span>
                      {assignee && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1E3A8A]/20 text-[#3B82F6]">{assignee.display_name}</span>}
                      {isMasterGm && (
                        <button data-testid={`raid-objective-remove-${o.id}`} onClick={() => removeObjective(o)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  );
                })}
                {(raid.objectives || []).length === 0 && <p className="text-xs text-[#f1e0c5]/30">No objectives yet.</p>}
              </div>
              {isMasterGm && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/10">
                  <Input data-testid="raid-objective-text-input" placeholder="New objective or assignment…" value={objectiveText} onChange={(e) => setObjectiveText(e.target.value)} className="bg-black/30 border-white/10 h-9 text-sm flex-1 min-w-[180px]" />
                  <Select value={objectiveAssignee} onValueChange={setObjectiveAssignee}>
                    <SelectTrigger data-testid="raid-objective-assignee-select" className="bg-black/30 border-white/10 h-9 w-44"><SelectValue placeholder="Unassigned (shared)" /></SelectTrigger>
                    <SelectContent>
                      {generals.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button data-testid="raid-objective-add-button" size="sm" className="h-9 rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] gap-1.5" onClick={addObjective}>
                    <Plus className="w-3.5 h-3.5" /> Add
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeOptionPanel === "goal" && showGoalButton && (
            <div className="panel-wood rounded-2xl p-5 space-y-2" data-testid="raid-options-dropdown-goal">
              <p className="text-[10px] text-[#f1e0c5]/40">Whether everyone's building their own path or fighting side by side, this is the one thing the Master GM decides for the whole table — leave it blank if you don't need one.</p>
              <Textarea
                data-testid="raid-party-goal-textarea"
                value={goalDraft}
                onChange={(e) => setGoalDraft(e.target.value)}
                onBlur={isMasterGm ? saveGoal : undefined}
                readOnly={!isMasterGm}
                placeholder="e.g. Everyone converges on the Sunken Temple to stop the ritual."
                className="bg-black/30 border-white/10 text-sm min-h-32"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="panel-wood rounded-2xl p-5 space-y-3" data-testid="raid-branches-panel">
            <h2 className="font-display text-xl flex items-center gap-2"><Swords className="w-4 h-4 text-[#D4AF37]" /> Tables &amp; Maps</h2>
            {raid.raid_type === "bbebbeg" && (
              <p className="text-[10px] text-[#f1e0c5]/30">Any General can make their own Table (their own map, own story path), or just help run NPCs on your Master Table via Helper Access below — either way, the BBEBBEG boss token itself always stays under your control alone.</p>
            )}
            {(raid.branches || []).length === 0 && <p className="text-xs text-[#f1e0c5]/30">No Tables yet — Generals self-serve their own once they join.</p>}
            {(raid.branches || []).map((b) => (
              <div key={b.id} data-testid={`raid-branch-${b.id}`} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Circle className={`w-2 h-2 shrink-0 ${b.general?.online ? "fill-green-500 text-green-500" : "fill-white/20 text-white/20"}`} />
                  <div>
                    <p className="text-sm">{b.name}</p>
                    <p className="text-[10px] text-[#f1e0c5]/40 uppercase tracking-wider">{b.general?.display_name || "Unassigned"} · {(b.scene_ids || []).length} scene{(b.scene_ids || []).length === 1 ? "" : "s"}</p>
                  </div>
                </div>
                {raid.raid_type === "dungeon_crawl" && (
                  b.dungeon_approved ? (
                    <span className="text-[10px] text-[#4d7c0f] uppercase tracking-wider">Approved</span>
                  ) : isMasterGm ? (
                    <Button data-testid={`raid-approve-dungeon-${b.id}`} size="sm" variant="secondary" className="rounded-full h-7 text-xs" onClick={() => approveDungeon(b.id)}>Approve Dungeon</Button>
                  ) : (
                    <span className="text-[10px] text-[#D90429] uppercase tracking-wider">Awaiting Approval</span>
                  )
                )}
                {(isMasterGm || b.assigned_general_member_id === myMember?.id) && (
                  <label className="flex items-center gap-1.5 cursor-pointer" title="Let any other General help run NPCs here">
                    <Checkbox data-testid={`raid-branch-helper-access-${b.id}`} checked={!!b.helper_access_enabled} onCheckedChange={(v) => toggleBranchHelperAccess(b.id, v)} />
                    <span className="text-[10px] text-[#f1e0c5]/40">Helper Access</span>
                  </label>
                )}
                {(isMasterGm || b.assigned_general_member_id === myMember?.id) && (
                  <button data-testid={`raid-copy-branch-invite-${b.id}`} onClick={() => copyBranchInvite(b.invite_code)} title="Copy Table invite link" className="text-[#f1e0c5]/40 hover:text-[#3B82F6] transition-colors duration-200">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {!raid.my_branch_id && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/10">
                <Input
                  data-testid="raid-make-branch-name-input"
                  placeholder={isMasterGm ? "Name an extra Table for this one-shot (e.g. Round 2 Arena)" : "Name your Table (e.g. Eastern Path)"}
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  className="bg-black/30 border-white/10 h-9 text-sm flex-1 min-w-[180px]"
                />
                <Button data-testid="raid-make-branch-button" size="sm" disabled={makingBranch} className="h-9 rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] gap-1.5" onClick={makeBranch}>
                  <Plus className="w-3.5 h-3.5" /> Make a Table
                </Button>
              </div>
            )}
            {isMasterGm && (
              <label className="flex items-center gap-2 pt-2 border-t border-white/10 cursor-pointer">
                <Checkbox data-testid="raid-shared-waiting-room-checkbox" checked={!!raid.shared_waiting_room} onCheckedChange={toggleSharedWaitingRoom} />
                <span className="text-[10px] text-[#f1e0c5]/50">Use ONE shared Waiting Room for every Table instead of each Table having its own private one (mark it from a scene tab on your Master table once enabled)</span>
              </label>
            )}
            {isMasterGm && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox data-testid="raid-master-helper-access-checkbox" checked={!!raid.master_helper_access_enabled} onCheckedChange={toggleMasterHelperAccess} />
                <span className="text-[10px] text-[#f1e0c5]/50">Let any General help run NPCs on your own Master Map scenes too</span>
              </label>
            )}
          </div>

          <div className="panel-wood rounded-2xl p-5 flex flex-col h-96" data-testid="raid-chat-panel">
            <h2 className="font-display text-xl mb-3">Raid Command Chat</h2>
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto scrollbar-thin space-y-2.5 pr-1">
              {messages.map((m) => (
                <div key={m.id} data-testid={`raid-chat-message-${m.id}`}>
                  <span className="text-xs font-semibold text-[#3B82F6]">{m.sender_name}</span>
                  <span className="text-[9px] uppercase tracking-wider text-[#f1e0c5]/30 ml-1.5">{m.sender_role === "master_gm" ? "Master GM" : "General"}</span>
                  <p className="text-sm text-[#f1e0c5]/85">{m.content}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="text-sm text-[#f1e0c5]/30 text-center pt-10">No messages yet. Coordinate the campaign here.</p>}
            </div>
            <div className="flex gap-2 pt-3 border-t border-white/10 mt-2">
              <input
                data-testid="raid-chat-input"
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Message the command chat…"
                className="flex-1 bg-black/30 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
              <button data-testid="raid-chat-send-button" onClick={sendChat} className="w-10 h-10 rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] flex items-center justify-center transition-colors duration-200">
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          <TournamentBracketPanel raid={raid} raidId={raidId} isMasterGm={isMasterGm} api={api} formatApiErrorDetail={formatApiErrorDetail} />
          <BbebbegPanel raid={raid} raidId={raidId} isMasterGm={isMasterGm} api={api} formatApiErrorDetail={formatApiErrorDetail} />
          <DungeonCrawlPanel raid={raid} raidId={raidId} isMasterGm={isMasterGm} api={api} formatApiErrorDetail={formatApiErrorDetail} />
          <CompleteRaidPanel raid={raid} raidId={raidId} isMasterGm={isMasterGm} api={api} formatApiErrorDetail={formatApiErrorDetail} onCompleted={(patch) => setRaid((r) => (r ? { ...r, ...patch } : r))} />
        </div>
      </div>
    </div>
  );
}
